import React, { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { BookOpen, Compass, ExternalLink, FileCheck, MapPinned, ShieldCheck } from "lucide-react";
import { NATIONAL_RESOURCES, jurisdictionFor, usesNswOutcomes } from "../../data/australianCurriculum";
import { useAuth } from "../../context/AuthContext";
import { JurisdictionSelect, saveJurisdiction } from "../../components/parent/JurisdictionPicker";

const CATEGORY_STYLE = {
  Registration: "bg-emerald-50 text-emerald-800",
  Curriculum: "bg-violet-50 text-violet-800",
  Planning: "bg-amber-50 text-amber-800",
  Records: "bg-sky-50 text-sky-800",
};

function ResourceLink({ resource }) {
  return (
    <a
      href={resource.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block rounded-xl border border-stone-200 bg-white p-4 transition hover:border-stone-400 hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700"
      data-testid="curriculum-resource"
    >
      <div className="flex justify-between items-start gap-3">
        <div className="min-w-0">
          {resource.category && (
            <span className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold ${CATEGORY_STYLE[resource.category] || "bg-stone-100 text-stone-700"}`}>
              {resource.category}
            </span>
          )}
          <div className="mt-2 text-sm font-bold leading-snug" style={{ color: "#1F3B2D" }}>{resource.title}</div>
        </div>
        <ExternalLink size={16} aria-hidden="true" className="shrink-0 text-stone-500 mt-1" />
      </div>
      <p className="mt-2 text-sm leading-relaxed text-stone-600">{resource.desc}</p>
      <span className="mt-2 block text-xs text-stone-500 break-all">{new URL(resource.url).hostname}</span>
      <span className="sr-only"> (opens a government or curriculum website in a new tab)</span>
    </a>
  );
}

function ChangeJurisdiction({ current }) {
  const { user, setSession } = useAuth();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState(current || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!code || code === current) { setOpen(false); return; }
    setSaving(true);
    try { await saveJurisdiction(code, setSession, user); toast.success("Learning location updated"); setOpen(false); }
    catch (err) { toast.error(err.response?.data?.detail || "Couldn't save that. Please try again."); }
    finally { setSaving(false); }
  };
  if (!open) {
    return <button type="button" onClick={() => { setCode(current || ""); setOpen(true); }} className="text-sm font-semibold underline text-emerald-800" data-testid="curriculum-change-state">Change your learning location</button>;
  }
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="curriculum-change-form">
      <div className="w-full sm:w-72"><JurisdictionSelect value={code} onChange={setCode} testid="curriculum-state-select" className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm" /></div>
      <button type="button" disabled={!code || saving} onClick={save} className="rounded-full bg-emerald-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="curriculum-state-save">{saving ? "Saving…" : "Save"}</button>
      <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-stone-700">Cancel</button>
    </div>
  );
}

function JurisdictionSection({ place }) {
  return (
    <section id={`curriculum-${place.code.toLowerCase()}`}
      className="paper-card p-4 sm:p-6 scroll-mt-24 space-y-4"
      aria-labelledby={`jurisdiction-heading-${place.code}`}
      data-testid={`curriculum-state-${place.code}`}>
      <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-800">
        <MapPinned size={15} aria-hidden="true" /> Your state or territory · {place.code}
      </span>
      <div>
        <h2 id={`jurisdiction-heading-${place.code}`} className="font-display text-2xl sm:text-3xl font-bold" style={{ color: "#1F3B2D" }}>{place.name}</h2>
        <p className="mt-1 text-xs text-stone-600"><strong>Responsible authority:</strong> {place.authority}</p>
      </div>
      <p className="text-sm leading-relaxed text-stone-700">{place.intro}</p>
      <div className="rounded-xl bg-stone-50 border border-stone-200 p-3 sm:p-4 flex gap-3">
        <FileCheck size={18} aria-hidden="true" className="shrink-0 mt-0.5 text-emerald-800" />
        <p className="text-sm leading-relaxed text-stone-700"><strong>Good to know:</strong> {place.focus}</p>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {place.links.map((resource) => <ResourceLink key={`${place.code}-${resource.title}`} resource={resource} />)}
      </div>
    </section>
  );
}

export default function Curriculum() {
  const { user } = useAuth();
  const place = jurisdictionFor(user?.jurisdiction);
  const nsw = usesNswOutcomes(user?.jurisdiction);
  const international = user?.jurisdiction === "OTHER";
  return (
    <div className="p-4 sm:p-6 lg:p-10 space-y-9 max-w-7xl mx-auto" data-testid="curriculum-page">
      <header className="space-y-4">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-800">
          <Compass size={17} aria-hidden="true" /> Your curriculum compass
        </div>
        <h1 data-tour="tour-curriculum" className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "#1F3B2D" }}>
          {international ? "Learning beyond Australia" : (place ? `Home education in ${place.name}` : "Home education in your state or territory")}
        </h1>
        <p className="text-base leading-relaxed text-stone-700 max-w-3xl">
          {international
            ? "Side-Quest was built around Australian home education, but families abroad can still use lessons, family planning, portfolios, and their own learning goal tracker. We do not provide local registration guidance or verified curriculum outcomes for countries outside Australia."
            : <>Side-Quest welcomes Australian families wherever they live. Here are official home education, curriculum and record-keeping resources for <strong>{place ? place.name : "your state or territory"}</strong>, plus national resources you may find useful.</>}
        </p>
        {user?.jurisdiction && <ChangeJurisdiction current={user.jurisdiction} />}
      </header>

      {place && <JurisdictionSection place={place} />}
      {international && <section className="paper-card p-5 space-y-3" data-testid="international-curriculum-notice">
        <h2 className="font-display text-xl font-bold text-emerald-950">Your family sets the learning trail</h2>
        <p className="text-sm text-stone-700">Choose subjects and define learning goals that make sense for your child. Track progress and link observations from the Evidence Backpack. Side-Quest will not insert Australian outcome codes or suggest that your program satisfies the rules where you live.</p>
        <Link className="inline-block font-bold text-sm underline text-emerald-900" to="/parent/outcomes">Open your family goal tracker →</Link>
      </section>}

      <section className="paper-card p-4 sm:p-6 border-l-4" style={{ borderLeftColor: "#4A5D3A" }} aria-labelledby="mapping-heading">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={20} className="text-emerald-800 shrink-0" aria-hidden="true" />
          <h2 id="mapping-heading" className="font-display text-xl font-bold" style={{ color: "#1F3B2D" }}>How Side-Quest fits with your local rules</h2>
        </div>
        <p className="text-sm leading-relaxed text-stone-700">
          Plan your learning, create and assign lessons, use your family calendar, see progress and collect the little wins in your Evidence Backpack.
          Side-Quest helps you organise learning; it doesn't register a child or certify a program, so check what your authority actually asks for.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-stone-600" data-testid="curriculum-outcome-note">
          {international
            ? "Outside Australia, outcomes are parent-defined. You can build lessons and learning plans, keep records, and link your own learning goals to evidence. Official curriculum mapping and local legal advice are not provided."
            : nsw
              ? "Automatic outcome suggestions and the learning plan writer use the NSW syllabus outcome list. They are suggestions for you to check, not official confirmation."
              : `Automatic outcome codes are only displayed where Side-Quest holds a verified reference for your jurisdiction. Always check against your own local curriculum.`}
        </p>
      </section>

      <section aria-labelledby="australia-wide-heading" className="space-y-4">
        <div className="flex items-center gap-2"><BookOpen aria-hidden="true" size={20} className="text-emerald-800" />
          <h2 id="australia-wide-heading" className="font-display text-2xl font-bold" style={{ color: "#1F3B2D" }}>Useful wherever you call home</h2>
        </div>
        <p className="text-sm text-stone-600 max-w-3xl">
          These Australian resources can provide optional teaching ideas and planning inspiration. They do not replace your local home education rules or establish a curriculum for international families.
        </p>
        <div className="grid md:grid-cols-3 gap-3" data-testid="curriculum-national-resources">
          {NATIONAL_RESOURCES.map((resource) => <ResourceLink key={resource.url} resource={resource} />)}
        </div>
      </section>

      <section className="rounded-2xl border border-stone-200 bg-stone-50 p-4 sm:p-6 space-y-3">
        <h2 className="font-display text-xl font-bold" style={{ color: "#1F3B2D" }}>A little help keeping track</h2>
        <p className="text-sm leading-relaxed text-stone-700">
          You don't have to collect everything at once. Start with your local registration guidance,
          build a realistic learning plan, then add lesson work, notes and everyday discoveries to the
          <strong> Evidence Backpack</strong> as you go. Check what your own authority actually asks for
          before preparing any report or review.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/parent/evidence" className="rounded-full bg-emerald-900 px-5 py-2.5 text-sm font-semibold text-white">Open Evidence Backpack</Link>
          <Link to="/parent/lessons" className="rounded-full border border-stone-400 bg-white px-5 py-2.5 text-sm font-semibold text-stone-800">My lessons</Link>
        </div>
        <p className="text-xs leading-relaxed text-stone-500">Official links and rules can change. Always check the current details on the linked authority's website.</p>
      </section>
    </div>
  );
}
